import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  isMultiCurrency,
  convertUnitPrice,
  roundQuotePrice,
  formatUnitPrice,
  type CurrencyCode,
} from "@forgepoint/shared";
import { type QuoteCurrencyFields } from "@/lib/quoteCurrency";
import { cn } from "@/lib/utils";

interface ConvertedUnitPriceTooltipProps {
  /** Unit price in the quote's base currency. */
  baseUnitPrice: string | null | undefined;
  /** Currency the price is stored in. */
  baseCurrencyCode?: CurrencyCode;
  /** Quote currency context; conversion only happens when multi-currency. */
  currencyContext?: QuoteCurrencyFields;
  /** Classes for the rendered price span. */
  className?: string;
}

/**
 * Renders a unit price in the quote currency, revealing the original
 * base-currency price on hover when the quote is multi-currency. With rounding
 * configured the converted unit snaps to the increment, matching the backend.
 * @param props - base unit price, its currency, quote currency context, and span classes
 * @returns the price span, hover-wrapped when multi-currency
 */
export function ConvertedUnitPriceTooltip({
  baseUnitPrice,
  baseCurrencyCode,
  currencyContext,
  className,
}: ConvertedUnitPriceTooltipProps) {
  const decimals = currencyContext?.quotePriceRounding?.decimals;
  const basePrice = (
    <span className={className}>
      {formatUnitPrice(baseUnitPrice ?? null, baseCurrencyCode, decimals)}
    </span>
  );

  if (!baseUnitPrice || !currencyContext || !isMultiCurrency(currencyContext)) {
    return basePrice;
  }

  const { snapshot, baseCurrency, quoteCurrency } = currencyContext;
  if (!snapshot || !baseCurrency || !quoteCurrency) {
    return basePrice;
  }

  const rawConverted = convertUnitPrice(baseUnitPrice, currencyContext);
  const converted = currencyContext.quotePriceRounding
    ? roundQuotePrice(rawConverted, currencyContext.quotePriceRounding)
    : rawConverted;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={cn("cursor-help", className)}>
            {formatUnitPrice(converted, quoteCurrency, decimals)}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{`${formatUnitPrice(baseUnitPrice, baseCurrency, decimals)} ${baseCurrency}`}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
